import { useEffect, useMemo } from 'react';

import debounce from 'lodash/debounce';

const useDebounceApi = ({ apiFn, delay = 500, dependencies = [], enableCache = true }) => {
  // Create memoized cache for results
  const cache = useMemo(() => {
    if (!enableCache) return null;
    return new Map();
  }, [enableCache]);

  // Create cached wrapper function
  const cachedApiFn = useMemo(() => {
    if (!enableCache) return apiFn;

    return (searchTerm, resolve) => {
      // Check cache first
      if (cache.has(searchTerm)) {
        // Use setTimeout to make cached results async (fixes loading state)
        setTimeout(() => resolve(cache.get(searchTerm)), 0);
        return;
      }

      // Call original function with caching resolve
      apiFn(searchTerm, (result) => {
        cache.set(searchTerm, result);
        resolve(result);
      });
    };
  }, [apiFn, enableCache, cache]);

  // Create debounced version
  const debouncedFn = useMemo(
    () => debounce(cachedApiFn, delay),
    [cachedApiFn, delay, ...dependencies]
  );

  // Cleanup
  useEffect(() => {
    return () => debouncedFn.cancel();
  }, [debouncedFn]);

  return {
    debouncedFn,
    clearCache: () => cache?.clear()
  };
};

export default useDebounceApi;
